import type { SupportedLanguage } from './types';

const SPANISH_COUNTRY_CODES = [
  'ES',
  'MX',
  'AR',
  'CO',
  'CL',
  'PE',
  'VE',
  'EC',
  'GT',
  'CU',
  'BO',
  'DO',
  'HN',
  'PY',
  'SV',
  'NI',
  'CR',
  'PA',
  'UY',
  'PR',
  'GQ',
] as const;

const PORTUGUESE_COUNTRY_CODES = ['BR', 'PT'] as const;

const GERMAN_COUNTRY_CODES = ['DE', 'AT', 'CH'] as const;

const FRENCH_COUNTRY_CODES = [
  'FR',
  'BE',
  'LU',
  'MC',
  'GF',
  'GP',
  'MQ',
  'RE',
  'NC',
  'PF',
  'PM',
  'WF',
  'YT',
  'BL',
  'MF',
] as const;

const ITALIAN_COUNTRY_CODES = ['IT', 'SM', 'VA'] as const;

function buildCountryLanguageMap(
  language: SupportedLanguage,
  countryCodes: readonly string[],
): Record<string, SupportedLanguage> {
  return Object.fromEntries(countryCodes.map((code) => [code, language]));
}

const COUNTRY_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  ...buildCountryLanguageMap('es', SPANISH_COUNTRY_CODES),
  ...buildCountryLanguageMap('pt-BR', PORTUGUESE_COUNTRY_CODES),
  ...buildCountryLanguageMap('de', GERMAN_COUNTRY_CODES),
  ...buildCountryLanguageMap('fr', FRENCH_COUNTRY_CODES),
  ...buildCountryLanguageMap('it', ITALIAN_COUNTRY_CODES),
};

/** The trimmed, upper-cased two-letter code, or `null` when missing or malformed. */
export function normalizeCountryCode(
  countryCode: string | null | undefined,
): string | null {
  const normalized = (countryCode ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

/**
 * Resolves a country code to a mapped language, or `null` when the code is
 * missing, malformed, or not explicitly mapped (i.e. ambiguous). Callers that
 * have a configured-locale fallback should prefer this so an unmapped country
 * degrades to the business's locale rather than a blind English default.
 */
export function resolveMappedLanguageFromCountryCode(
  countryCode: string | null | undefined,
): SupportedLanguage | null {
  const normalized = normalizeCountryCode(countryCode);
  return normalized ? (COUNTRY_LANGUAGE_MAP[normalized] ?? null) : null;
}

export function resolveLanguageFromCountryCode(
  countryCode: string | null | undefined,
): SupportedLanguage {
  return resolveMappedLanguageFromCountryCode(countryCode) ?? 'en';
}
